import { useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { WorkerProfileField } from 'types';
import { GOV_ID_TYPE_OTHER_OPTION_ID, WORKER_NAME_MAX_LENGTH } from 'constants/common';
import { FieldType, IField } from 'modules/views/pages/PersonalInformation/components/fieldWrapper';

import { IProfileFieldConstraintsMap } from 'types/profileCustomFields';
import { useAppSelector } from 'modules/state-mgmt/store';
import { GENDER_OPTIONS, YES_NO_OPTIONS, SUPERVISOR_OPTIONS, SKILLED_NON_SKILLED_OPTIONS } from 'utils/optionsUtil';
import { IProfileInformationSchema } from '../schema';

const nameInputProps = { inputProps: { maxLength: WORKER_NAME_MAX_LENGTH } };

export const PROFILE_INFO_FIELD_CONFIG: Partial<
  Record<WorkerProfileField, { displayNameKey: string; formField: string }>
> = {
  // Personal Information
  [WorkerProfileField.workerFirstName]: {
    displayNameKey: 'personalInformation.personalInformation.firstName',
    formField: 'firstName',
  },
  [WorkerProfileField.workerMiddleName]: {
    displayNameKey: 'personalInformation.personalInformation.middleName',
    formField: 'middleName',
  },
  [WorkerProfileField.workerLastName]: {
    displayNameKey: 'personalInformation.personalInformation.lastName',
    formField: 'lastName',
  },
  [WorkerProfileField.workerDateOfBirth]: {
    displayNameKey: 'personalInformation.personalInformation.dateOfBirth',
    formField: 'dateOfBirth',
  },
  [WorkerProfileField.workerGender]: {
    displayNameKey: 'personalInformation.personalInformation.gender',
    formField: 'gender',
  },
  [WorkerProfileField.workerEthnicity]: {
    displayNameKey: 'personalInformation.personalInformation.ethnicity',
    formField: 'ethnicityId',
  },
  [WorkerProfileField.workerPrimaryLanguage]: {
    displayNameKey: 'personalInformation.personalInformation.primaryLanguage',
    formField: 'primaryLanguageId',
  },
  [WorkerProfileField.workerVeteranStatus]: {
    displayNameKey: 'personalInformation.personalInformation.veteranStatus',
    formField: 'isVeteran',
  },
  [WorkerProfileField.workerAllergies]: {
    displayNameKey: 'personalInformation.personalInformation.allergies',
    formField: 'allergies',
  },
  [WorkerProfileField.workerLastSSN]: {
    displayNameKey: 'personalInformation.personalInformation.lastSSN',
    formField: 'socialSecurityNumber',
  },
  [WorkerProfileField.workerProfilePhoto]: {
    displayNameKey: 'personalInformation.profilePhoto',
    formField: 'pictureUrl',
  },

  // Government ID
  [WorkerProfileField.workerGovtIdType]: {
    displayNameKey: 'personalInformation.govtId.idType',
    formField: 'identificationTypeId',
  },
  [WorkerProfileField.workerGovtIdIssuedBy]: {
    displayNameKey: 'personalInformation.govtId.issuedBy',
    formField: 'identificationGeographicLocationId',
  },
  [WorkerProfileField.workerGovtIdNumber]: {
    displayNameKey: 'personalInformation.govtId.idNumber',
    formField: 'identificationNumber',
  },
  [WorkerProfileField.workerGovtIdImage]: {
    displayNameKey: 'personalInformation.govtId.idImage',
    formField: 'governmentDocumentFiles',
  },

  // Contact Information
  [WorkerProfileField.email]: {
    displayNameKey: 'contactInformation.contactInfo.email',
    formField: 'email',
  },
  [WorkerProfileField.mobilePhoneNumber]: {
    displayNameKey: 'contactInformation.contactInfo.mobileNumber',
    formField: 'mobileNumber',
  },
  [WorkerProfileField.alternateContactMobilePhoneNumber]: {
    displayNameKey: 'contactInformation.contactInfo.alternatePhone',
    formField: 'phoneNumber',
  },

  // Home Address
  [WorkerProfileField.homeAddressLine1]: {
    displayNameKey: 'contactInformation.homeAddress.addressLine1',
    formField: 'addressLine1',
  },
  [WorkerProfileField.homeAddressLine2]: {
    displayNameKey: 'contactInformation.homeAddress.addressLine2',
    formField: 'addressLine2',
  },
  [WorkerProfileField.homeAddressCountry]: {
    displayNameKey: 'contactInformation.homeAddress.country',
    formField: 'country',
  },
  [WorkerProfileField.homeAddressStateProvince]: {
    displayNameKey: 'contactInformation.homeAddress.state',
    formField: 'state',
  },
  [WorkerProfileField.homeAddressCity]: {
    displayNameKey: 'contactInformation.homeAddress.city',
    formField: 'city',
  },
  [WorkerProfileField.homeAddressZipPostal]: {
    displayNameKey: 'contactInformation.homeAddress.zipCode',
    formField: 'zipCode',
  },

  // Emergency Contact
  [WorkerProfileField.emergencyContactName]: {
    displayNameKey: 'contactInformation.emergencyContact.name',
    formField: 'emergencyContactName',
  },
  [WorkerProfileField.emergencyContactMobilePhone]: {
    displayNameKey: 'contactInformation.emergencyContact.mobileNumber',
    formField: 'emergencyContactNumber',
  },
  [WorkerProfileField.emergencyContactRelationship]: {
    displayNameKey: 'contactInformation.emergencyContact.relationship',
    formField: 'emergencyContactRelationship',
  },

  // Trades
  [WorkerProfileField.workerRole]: {
    displayNameKey: 'trades.role',
    formField: 'isSupervisor',
  },
  [WorkerProfileField.workerLaborUnionStatus]: {
    displayNameKey: 'trades.laborUnion',
    formField: 'isAffiliatedToLaborUnion',
  },
  [WorkerProfileField.workerWorkExperience]: {
    displayNameKey: 'trades.workExperience',
    formField: 'isSkilled',
  },
  [WorkerProfileField.workerLaborUnionNumber]: {
    displayNameKey: 'trades.laborUnionNumber',
    formField: 'laborUnionNumber',
  },
  [WorkerProfileField.workerTrades]: {
    displayNameKey: 'trades.selectedTrades',
    formField: 'trades',
  },
};

export const useProfileInformationFields = (
  profileFieldConstraintsMap: IProfileFieldConstraintsMap,
  values: IProfileInformationSchema,
) => {
  const { t } = useTranslation();

  const { ethnicities, languages, languageCode, idTypes, states, countries, skilledTrades } = useAppSelector(state => ({
    ethnicities: state.cache.ethnicities[state.app?.languageCode || 'en'] || [],
    languages: state.cache.languages[state.app?.languageCode || 'en'] || [],
    languageCode: state.app?.languageCode || 'en',
    idTypes: state.cache.iDTypes[state.app?.languageCode || 'en'] || [],
    states: state.cache.states[state.app?.languageCode || 'en'] || [],
    countries: state.cache.countries?.[state.app?.languageCode || 'en'] || [],
    skilledTrades: state.cache.skilledTrades?.[state.app?.languageCode || 'en'] || [],
  }));

  const getFieldDisplayName = useCallback(
    (field: WorkerProfileField): string => {
      const config = PROFILE_INFO_FIELD_CONFIG[field];
      return config ? t(config.displayNameKey) : field.toString();
    },
    [t],
  );

  const formFieldMap: Partial<Record<WorkerProfileField, string>> = useMemo(
    () =>
      Object.entries(PROFILE_INFO_FIELD_CONFIG).reduce(
        (acc, [field, config]) => {
          acc[field as WorkerProfileField] = config.formField;
          return acc;
        },
        {} as Partial<Record<WorkerProfileField, string>>,
      ),
    [],
  );

  const createField = useCallback(
    (workerProfileField: WorkerProfileField, fieldType: FieldType, fieldOptions?: any[], extraProps?: any): IField => {
      const formFieldId = formFieldMap[workerProfileField];
      const constraints = profileFieldConstraintsMap[workerProfileField];
      return {
        id: formFieldId,
        name: getFieldDisplayName(workerProfileField),
        type: fieldType,
        isRequired: constraints?.isMandatory || !!constraints?.isSkippable,
        isVisible: true,
        options: fieldOptions,
        extraProps,
        showSkippableIndicator: false,
        isSkippable: constraints?.isSkippable,
        skippableTill: constraints?.skippableTill,
      };
    },
    [getFieldDisplayName, formFieldMap, profileFieldConstraintsMap],
  );

  // ── Personal Information fields ──────────────────────────────────────
  const personalInformationFields: IField[] = useMemo(
    () => [
      createField(WorkerProfileField.workerFirstName, FieldType.Text, undefined, nameInputProps),
      createField(WorkerProfileField.workerMiddleName, FieldType.Text, undefined, nameInputProps),
      createField(WorkerProfileField.workerLastName, FieldType.Text, undefined, nameInputProps),
      createField(WorkerProfileField.workerDateOfBirth, FieldType.Date),
      createField(WorkerProfileField.workerEthnicity, FieldType.Picker, ethnicities),
      createField(WorkerProfileField.workerPrimaryLanguage, FieldType.Picker, languages),
      createField(WorkerProfileField.workerVeteranStatus, FieldType.Picker, YES_NO_OPTIONS[languageCode] || []),
      createField(WorkerProfileField.workerAllergies, FieldType.Text),
      createField(WorkerProfileField.workerLastSSN, FieldType.Text, undefined, { inputProps: { maxLength: 4 } }),
    ],
    [createField, ethnicities, languages, languageCode],
  );

  const genderSectionFields: IField[] = useMemo(
    () => [createField(WorkerProfileField.workerGender, FieldType.Radio, GENDER_OPTIONS[languageCode] || [])],
    [createField, languageCode],
  );

  const profilePhotoField = useMemo(
    () => ({
      id: 'pictureUrl',
      name: getFieldDisplayName(WorkerProfileField.workerProfilePhoto),
      isRequired:
        profileFieldConstraintsMap[WorkerProfileField.workerProfilePhoto]?.isMandatory ||
        !!profileFieldConstraintsMap[WorkerProfileField.workerProfilePhoto]?.isSkippable,
      isVisible: true,
      showSkippableIndicator: false,
    }),
    [getFieldDisplayName, profileFieldConstraintsMap],
  );

  // ── Government ID fields ─────────────────────────────────────────────
  const govtIdFields: IField[] = useMemo(() => {
    const fields: IField[] = [createField(WorkerProfileField.workerGovtIdType, FieldType.Picker, idTypes)];

    const issuedByField: IField = {
      id:
        values.identificationTypeId === GOV_ID_TYPE_OTHER_OPTION_ID
          ? 'otherIdentificationType'
          : 'identificationGeographicLocationId',
      name: getFieldDisplayName(WorkerProfileField.workerGovtIdIssuedBy),
      type: values.identificationTypeId === GOV_ID_TYPE_OTHER_OPTION_ID ? FieldType.Text : FieldType.Picker,
      isRequired:
        profileFieldConstraintsMap[WorkerProfileField.workerGovtIdIssuedBy]?.isMandatory ||
        !!profileFieldConstraintsMap[WorkerProfileField.workerGovtIdIssuedBy]?.isSkippable,
      isVisible: true,
      options:
        states?.length > 0 && values.identificationTypeId && values.identificationTypeId !== GOV_ID_TYPE_OTHER_OPTION_ID
          ? states.filter(
              (d: any) =>
                idTypes.find((e: any) => e.id === values.identificationTypeId)?.geographicLocationsIds?.indexOf(d.id) >=
                0,
            )
          : [],
      showSkippableIndicator: false,
    };

    fields.push(issuedByField);
    fields.push(createField(WorkerProfileField.workerGovtIdNumber, FieldType.Text));

    return fields;
  }, [createField, values, getFieldDisplayName, profileFieldConstraintsMap, idTypes, states]);

  const govtIdImageField = useMemo(
    () => ({
      id: 'governmentDocumentFiles',
      name: getFieldDisplayName(WorkerProfileField.workerGovtIdImage),
      isRequired:
        profileFieldConstraintsMap[WorkerProfileField.workerGovtIdImage]?.isMandatory ||
        !!profileFieldConstraintsMap[WorkerProfileField.workerGovtIdImage]?.isSkippable,
      isVisible: true,
      showSkippableIndicator: false,
    }),
    [getFieldDisplayName, profileFieldConstraintsMap],
  );

  // ── Contact Information fields ───────────────────────────────────────
  const contactInfoFields = useMemo(
    () => ({
      email: {
        id: 'email',
        name: getFieldDisplayName(WorkerProfileField.email),
        isRequired:
          profileFieldConstraintsMap[WorkerProfileField.email]?.isMandatory ||
          !!profileFieldConstraintsMap[WorkerProfileField.email]?.isSkippable,
      },
      mobileNumber: {
        id: 'mobileNumber',
        name: getFieldDisplayName(WorkerProfileField.mobilePhoneNumber),
        isRequired:
          profileFieldConstraintsMap[WorkerProfileField.mobilePhoneNumber]?.isMandatory ||
          !!profileFieldConstraintsMap[WorkerProfileField.mobilePhoneNumber]?.isSkippable,
      },
      phoneNumber: {
        id: 'phoneNumber',
        name: getFieldDisplayName(WorkerProfileField.alternateContactMobilePhoneNumber),
        isRequired:
          profileFieldConstraintsMap[WorkerProfileField.alternateContactMobilePhoneNumber]?.isMandatory ||
          !!profileFieldConstraintsMap[WorkerProfileField.alternateContactMobilePhoneNumber]?.isSkippable,
      },
    }),
    [getFieldDisplayName, profileFieldConstraintsMap],
  );

  // ── Home Address fields ──────────────────────────────────────────────
  const addressFields: IField[] = useMemo(
    () => [
      createField(WorkerProfileField.homeAddressLine1, FieldType.Text),
      createField(WorkerProfileField.homeAddressLine2, FieldType.Text),
      createField(WorkerProfileField.homeAddressCountry, FieldType.Picker, countries),
      createField(WorkerProfileField.homeAddressStateProvince, FieldType.Picker, states),
      createField(WorkerProfileField.homeAddressCity, FieldType.Text),
      createField(WorkerProfileField.homeAddressZipPostal, FieldType.Text),
    ],
    [createField, countries, states],
  );

  const allowedCountryCodes = useMemo(() => countries.map((c: any) => c.code), [countries]);

  // ── Emergency Contact fields ─────────────────────────────────────────
  const emergencyContactFields: IField[] = useMemo(
    () => [
      createField(WorkerProfileField.emergencyContactName, FieldType.Text, undefined, { autoComplete: 'new-password' }),
      createField(WorkerProfileField.emergencyContactRelationship, FieldType.Text, undefined, {
        autoComplete: 'new-password',
      }),
    ],
    [createField],
  );

  const emergencyPhoneField = useMemo(
    () => ({
      id: 'emergencyContactNumber',
      name: getFieldDisplayName(WorkerProfileField.emergencyContactMobilePhone),
      isRequired:
        profileFieldConstraintsMap[WorkerProfileField.emergencyContactMobilePhone]?.isMandatory ||
        !!profileFieldConstraintsMap[WorkerProfileField.emergencyContactMobilePhone]?.isSkippable,
    }),
    [getFieldDisplayName, profileFieldConstraintsMap],
  );

  // ── Worker Trades fields ─────────────────────────────────────────────
  const otherTradesSentinel = useMemo(
    () => skilledTrades.find((t: any) => ['other', 'otro'].includes(t?.name?.toLowerCase())),
    [skilledTrades],
  );

  const masterListTrades = useMemo(
    () => skilledTrades.filter((t: any) => t?.masterList && t?.id !== otherTradesSentinel?.id) || [],
    [skilledTrades, otherTradesSentinel],
  );

  const nonMasterListTrades = useMemo(() => skilledTrades.filter((t: any) => !t?.masterList) || [], [skilledTrades]);

  const roleFields: IField[] = useMemo(
    () => [
      createField(WorkerProfileField.workerRole, FieldType.Picker, SUPERVISOR_OPTIONS[languageCode] || []),
      createField(WorkerProfileField.workerLaborUnionStatus, FieldType.Picker, YES_NO_OPTIONS[languageCode] || []),
      createField(
        WorkerProfileField.workerWorkExperience,
        FieldType.Picker,
        SKILLED_NON_SKILLED_OPTIONS[languageCode] || [],
      ),
      createField(WorkerProfileField.workerLaborUnionNumber, FieldType.Text),
    ],
    [createField, languageCode],
  );

  return {
    personalInformationFields,
    genderSectionFields,
    profilePhotoField,
    govtIdFields,
    govtIdImageField,
    contactInfoFields,
    addressFields,
    allowedCountryCodes,
    emergencyContactFields,
    emergencyPhoneField,
    roleFields,
    masterListTrades,
    nonMasterListTrades,
    isTradesRequired: profileFieldConstraintsMap[WorkerProfileField.workerTrades]?.isMandatory || false,
  };
};
